// @ts-strict-ignore
import { deleteProperty, setProperty } from 'dot-prop';

import { ActionType } from '../../engine/constants';
import { ReduxErrorTypes } from '../constants';
import type { SchemaInstance } from '../../engine/types';
import { WorkflowEngineException } from '../../engine/schema';
import type { WorkflowState } from '../state';

import { isActionOfType } from '../../../service/actions';
import {
  insertIntoExpression,
  assignDefaultsToDataRoute,
  modifyRelevantDataFromDataRouteRemoval,
} from '../../engine/update/expression';
import {
  compileWorkflowDataDependencies,
  snapshotAndCloneWorkflowData,
} from '../helpers';

// ================ Data Routes Reducers ================

export function removeDataRoute(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      allowEmptyParentGroup?: boolean;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { id: dataRouteId, allowEmptyParentGroup } = action.payload;

  const targetDataRoute = workflowData.dataRoutes[dataRouteId];
  deleteProperty(workflowData.dataRoutes, dataRouteId);
  modifyRelevantDataFromDataRouteRemoval({
    targetDataRoute,
    dataRouteId,
    workflowData,
    allowEmptyParentGroup,
  });
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function createDataRoute(
  state: WorkflowState,
  action: {
    payload: {
      path: string;
      value: any;
      expressionPath: string;
      insertion?: boolean;
      isPreview?: boolean;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { workflowProps } = state;
  const {
    expressionPath, path, value, insertion, isPreview,
  } = action.payload;
  const dataRouteId = path.split('.')[0];

  const newDataRoute = assignDefaultsToDataRoute({
    path: '',
    dataRoute: value,
    workflowData: (workflowData as SchemaInstance),
    workflowProps,
    value,
  });
  workflowData.dataRoutes[dataRouteId] = newDataRoute;

  const targetAction = workflowData.actions[value.targetId];
  if (isActionOfType(targetAction, ActionType.OUTPUT)) {
    const { expression } = insertIntoExpression({
      expression: targetAction.output.expression,
      path: expressionPath || '',
      value: { data_route_id: dataRouteId },
      insertion,
    });

    workflowData.actions[value.targetId].output.expression = expression;
  }
  state.workflowData = isPreview
    ? workflowData
    : compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function updateDataRouteValue(
  state: WorkflowState,
  action: {
    payload: {
      path: string;
      value: any;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { workflowProps } = state;
  const { path, value } = action.payload;
  const [dataRouteId, ...dataRoutePathValues] = path.split('.');
  const dataRoutePath = dataRoutePathValues.join('.');
  const originalDataRoute = workflowData.dataRoutes[dataRouteId];

  if (!originalDataRoute) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_DATAROUTE_ID);
  }

  if (['sourceId', 'attribute.id'].includes(dataRoutePath)) {
    const newDataRoute = assignDefaultsToDataRoute({
      path: dataRoutePath,
      dataRoute: originalDataRoute,
      workflowData: (workflowData as SchemaInstance),
      workflowProps,
      value,
    });
    workflowData.dataRoutes[dataRouteId] = newDataRoute;
  } else {
    workflowData.dataRoutes = setProperty(workflowData.dataRoutes, path, value);
  }

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}
